import { createContext, useCallback, useContext, useRef, useState } from "react";

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const scheduleRemoval = useCallback((id, delay = 3500) => {
    clearTimeout(timers.current[id]);
    timers.current[id] = setTimeout(() => {
      setToasts(t => t.filter(x => x.id !== id));
      delete timers.current[id];
    }, delay);
  }, []);

  // addToast — returns the id so callers can update/dismiss it later
  const addToast = useCallback((message, type = "success", { persist = false } = {}) => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, message, type }]);
    if (!persist) scheduleRemoval(id);
    return id;
  }, [scheduleRemoval]);

  // updateToast — swap message+type on an existing toast then auto-dismiss
  const updateToast = useCallback((id, message, type = "success") => {
    setToasts(t => t.map(x => x.id === id ? { ...x, message, type } : x));
    scheduleRemoval(id, 3500);
  }, [scheduleRemoval]);

  // dismissToast — immediately remove
  const dismissToast = useCallback((id) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setToasts(t => t.filter(x => x.id !== id));
  }, []);

  const ctx = { addToast, updateToast, dismissToast };

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <div className="toast-container" aria-live="polite" aria-atomic="false">
        {toasts.map(t => (
          <div key={t.id} className={`toast toast-${t.type}`} role="status">
            {t.type === "loading" && (
              <span className="spinner toast-spinner" style={{ marginRight: 8, flexShrink: 0 }} aria-hidden="true" />
            )}
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// Backward-compatible — all existing callers do `const toast = useToast()` and call it as a function
export function useToast() {
  const { addToast } = useContext(ToastContext);
  return addToast;
}

// Extended hook for AdminDashboard: returns { addToast, updateToast, dismissToast }
export function useToastActions() {
  return useContext(ToastContext);
}
